import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { api } from '../../services/api';
import { toast } from '../../store/toastStore';
import { apiErrorMessage } from '../sales/shared';
import { reasonOk } from './changeRequestModel';
import type { ChangeRequestType } from '../../types';

/**
 * FIX-EDIT-AFTER-LOCK (D-347 §4) - "בקשת שינוי": shown where a direct edit is refused (attendance in a
 * closed month, a log a report was already produced on). The caller builds WHAT changes (`payload`);
 * this box asks for the reason (required) and sends the request to the OTE managers.
 */
export default function ChangeRequestBox({ type, payload, hint, disabled, onSent, idPrefix }: {
  type: ChangeRequestType;
  /** Built at send time from the edited fields; null = nothing to send yet. */
  payload: () => Record<string, unknown> | null;
  hint?: string;
  disabled?: boolean;
  onSent?: () => void;
  idPrefix: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const send = useMutation({
    mutationFn: () => {
      const p = payload();
      if (!p) throw { error: { message: t('changeRequests.error') } };
      return api.createChangeRequest({ type, payload: p, reason: reason.trim() });
    },
    onSuccess: () => {
      toast('success', t('changeRequests.sent'));
      void qc.invalidateQueries({ queryKey: ['change-requests'] });
      setReason('');
      onSent?.();
    },
    onError: (err) => setError(apiErrorMessage(err, t('changeRequests.error'))),
  });
  const reasonId = `${idPrefix}-cr-reason`;
  return (
    <div className="rounded-lg border border-border bg-surface-subtle p-3 space-y-2" data-testid="change-request-box">
      <p className="text-sm font-medium text-text">{t('changeRequests.title')}</p>
      {hint && <p className="text-sm text-text-2">{hint}</p>}
      <label htmlFor={reasonId} className="label">{t('changeRequests.reasonLabel')} <span className="text-danger-700" aria-hidden="true">*</span></label>
      <textarea id={reasonId} rows={2} className="input" value={reason} aria-required="true" disabled={send.isPending}
        onChange={(e) => { setReason(e.target.value); if (error) setError(''); }} data-testid="change-request-reason" />
      {error && <p role="alert" className="text-sm text-danger-700">{error}</p>}
      <button type="button" className="btn btn-primary btn-sm" disabled={disabled || send.isPending || !reasonOk(reason)}
        onClick={() => send.mutate()} data-testid="change-request-send">
        <Send size={14} aria-hidden="true" />{t('changeRequests.send')}
      </button>
    </div>
  );
}
